import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Artifact, ArtifactType, SnaphotReference } from '@/models'
import { IArtifactService } from '@/services/IArtifactService'
import { ISearchService } from '@/services/ISearchService'
import { useBaseStore } from '.'
import { groupBy } from '@/utils/helpers'

/**
 * Artifact Cache Store
 *
 * Manages artifact data caching, artifact types, and search functionality.
 * Pure data management without UI state.
 */
export const useArtifactCacheStore = defineStore('artifactCacheStore', () => {
  // State
  const artifacts = ref<Artifact[]>([])
  const artifactTypes = ref<ArtifactType[]>([])
  const currentSnapshotId = ref<string | undefined>(undefined)

  // Getters
  const getArtifactById = computed(() => {
    return (id: number): Artifact | undefined => {
      return artifacts.value.find((a) => a.id === id)
    }
  })

  const getArtifactsForType = computed(() => {
    return (typeId: number): Artifact[] => {
      return artifacts.value.filter((a) => a.artifactType.id === typeId)
    }
  })

  const getArtifactType = computed(() => {
    return (id: number): ArtifactType | undefined => {
      return artifactTypes.value.find((at) => at.id === id)
    }
  })

  const getArtifactTypeForArtifact = computed(() => {
    return (artifact: Artifact): ArtifactType | undefined => {
      return artifactTypes.value.find((a) => artifact.artifactType.id === a.id)
    }
  })

  /** Map from artifact id to artifact for O(1) lookup */
  const artifactsById = computed(() => new Map(artifacts.value.map((a) => [a.id, a])))

  /** Map from artifact type id to all artifacts of that type */
  const artifactsByType = computed(() => groupBy(artifacts.value, (a) => a.artifactType.id))

  // Actions

  /**
   * Load a single artifact by ID
   */
  async function loadArtifactById(params: {
    artifactService: IArtifactService
    projectId: number
    snapshotId: SnaphotReference
    artifactId: number
    filterId?: number
  }): Promise<Artifact | undefined> {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    try {
      const artifact = await params.artifactService.getArtifact(
        params.projectId,
        params.snapshotId,
        params.artifactId,
        params.filterId,
      )

      if (artifact) {
        setArtifacts([artifact])
        extractArtifactTypesFromArtifacts()
        currentSnapshotId.value = params.snapshotId.toString()
      }

      return artifact
    } catch (error) {
      console.error('Error loading artifact:', error)
      return undefined
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  /**
   * Load linked artifacts for a given artifact ID
   */
  async function loadLinkedArtifacts(params: {
    artifactService: IArtifactService
    projectId: number
    snapshotId: SnaphotReference
    artifactId: number
    filterId?: number
    excludeArtifacts?: boolean
  }): Promise<{ artifacts: Artifact[] }> {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    try {
      const response = await params.artifactService.getLinkedArtifacts(
        params.projectId,
        params.snapshotId,
        params.artifactId,
        'any',
        params.filterId,
      )

      if (!params.excludeArtifacts) {
        updateArtifacts(response.artifacts)
      }
      extractArtifactTypesFromArtifacts()
      currentSnapshotId.value = params.snapshotId.toString()

      return { artifacts: response.artifacts }
    } catch (error) {
      console.error('Error loading linked artifacts:', error)
      return { artifacts: [] }
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  /**
   * Load artifacts collection by type
   */
  async function loadArtifactsCollection(params: {
    artifactService: IArtifactService
    projectId: number
    snapshotId: SnaphotReference
    typeId: number
    filterId?: number
    include?: string
    sortBy?: string
    page?: number
    pageSize?: number
  }): Promise<Artifact[]> {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    try {
      const artifacts = await params.artifactService.getArtifactsCollection(
        params.projectId,
        params.snapshotId,
        params.typeId,
        params.filterId,
        params.include,
        params.sortBy,
        params.page,
        params.pageSize,
      )

      updateArtifacts(artifacts)
      return artifacts
    } catch (error) {
      console.error('Error loading artifacts collection:', error)
      return []
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  /**
   * Load artifact types for a snapshot
   */
  async function loadArtifactTypes(params: {
    artifactService: IArtifactService
    projectId: number
    snapshotId: SnaphotReference
    filterId?: number
  }): Promise<void> {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    try {
      const types = await params.artifactService.getArtifactTypes(
        params.projectId,
        params.snapshotId,
        params.filterId,
      )

      setArtifactTypes(types)
    } catch (error) {
      console.error('Error loading artifact types:', error)
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  /**
   * Search artifacts for a specific artifact type
   */
  async function searchArtifactsForType(params: {
    searchService: ISearchService
    artifactType: ArtifactType
    searchString: string
    projectId: number
    snapshotId: SnaphotReference
    page: number
    filterId?: number
    itemsPerPage?: number
  }): Promise<{ artifacts: Artifact[]; totalElements?: number }> {
    const baseStore = useBaseStore()
    baseStore.startNewJob()

    let searchString = params.searchString
    if (
      !searchString.startsWith('+') &&
      !searchString.startsWith('-') &&
      !searchString.startsWith('(')
    ) {
      searchString = '+' + searchString
    }

    const maskedArtifactType = params.artifactType.name.replaceAll(
      /([-+=&|><!(){}[\]^"~*?:\\/])/g,
      '\\\\$1',
    )

    try {
      const response = await params.searchService.searchSnapshotArtifacts(
        `+(artifactTypeName:"${maskedArtifactType}") ${searchString}`,
        params.projectId,
        params.snapshotId,
        params.page,
        params.filterId,
        params.itemsPerPage,
      )

      setArtifactsForType(params.artifactType, response.data)

      return {
        artifacts: response.data,
        totalElements: response.pagination?.totalElements,
      }
    } catch (error) {
      console.error('Error in searchArtifactsForType:', error)
      return { artifacts: [] }
    } finally {
      baseStore.decrementRunningJobs()
    }
  }

  /**
   * Set artifacts (replace all)
   */
  function setArtifacts(newArtifacts: Artifact[]): void {
    artifacts.value = newArtifacts
  }

  /**
   * Update artifacts (merge without duplicates)
   */
  function updateArtifacts(newArtifacts: Artifact[]): void {
    newArtifacts.forEach((newArtifact) => {
      const exists = artifacts.value.some((artifact) => artifact.id === newArtifact.id)
      if (!exists) {
        artifacts.value.push(newArtifact)
      }
    })
  }

  /**
   * Set artifacts for a specific type (replace only that type's artifacts)
   */
  function setArtifactsForType(artifactType: ArtifactType, newArtifacts: Artifact[]): void {
    // Remove all artifacts of this type
    artifacts.value = artifacts.value.filter((a) => a.artifactType.id !== artifactType.id)
    // Add new artifacts
    artifacts.value = artifacts.value.concat(newArtifacts)
  }

  /**
   * Set artifact types (replace all)
   */
  function setArtifactTypes(types: ArtifactType[]): void {
    artifactTypes.value = types
  }

  /**
   * Update artifact types (merge without duplicates)
   */
  function updateArtifactTypes(types: ArtifactType[]): void {
    types.forEach((newType) => {
      const exists = artifactTypes.value.some((at) => at.id === newType.id)
      if (!exists) {
        artifactTypes.value.push(newType)
      }
    })
  }

  /**
   * Extract artifact types from currently loaded artifacts
   */
  function extractArtifactTypesFromArtifacts(): void {
    artifacts.value.forEach((artifact) => {
      const typeExists = artifactTypes.value.some((at) => at.id === artifact.artifactType.id)
      if (!typeExists) {
        artifactTypes.value.push(artifact.artifactType)
      }
    })
  }

  /**
   * Clear all artifacts
   */
  function clearArtifacts(): void {
    artifacts.value = []
  }

  /**
   * Clear all artifact types
   */
  function clearArtifactTypes(): void {
    artifactTypes.value = []
  }

  /**
   * Clear all cache
   */
  function clearAll(): void {
    clearArtifacts()
    clearArtifactTypes()
    currentSnapshotId.value = undefined
  }

  // Return public API
  return {
    // State
    artifacts,
    artifactTypes,
    currentSnapshotId,
    // Getters
    getArtifactById,
    artifactsById,
    artifactsByType,
    getArtifactsForType,
    getArtifactType,
    getArtifactTypeForArtifact,
    // Actions
    loadArtifactById,
    loadLinkedArtifacts,
    loadArtifactsCollection,
    loadArtifactTypes,
    searchArtifactsForType,
    setArtifacts,
    updateArtifacts,
    setArtifactsForType,
    setArtifactTypes,
    updateArtifactTypes,
    extractArtifactTypesFromArtifacts,
    clearArtifacts,
    clearArtifactTypes,
    clearAll,
  }
})
